
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useReactFlow, Node } from 'reactflow';
import { Schedule } from '../../types';

interface SearchOverlayProps {
  nodes: Node<Schedule>[];
  onSelectNode: (schedule: Schedule) => void;
}

const SearchOverlay: React.FC<SearchOverlayProps> = ({ nodes, onSelectNode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setCenter, setNodes } = useReactFlow();

  // 1. Identify matches based on Name and Purpose (Description)
  const matches = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return nodes.filter(node => {
      const data = node.data;
      return (
        data.name.toLowerCase().includes(term) ||
        data.purpose.toLowerCase().includes(term) ||
        data.group.toLowerCase().includes(term)
      );
    });
  }, [nodes, searchTerm]);

  // 2. Keyboard shortcut logic (Ctrl+F / Cmd+F)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setIsVisible(true);
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // 3. Navigation and Zoom logic
  const navigateToMatch = (index: number, isInitial: boolean = false) => {
    const match = matches[index];
    if (!match) return;

    // Auto-select the node to open the side inspector
    onSelectNode(match.data);
    
    // Highlight in ReactFlow
    setNodes((nds) => 
      nds.map((n) => ({
        ...n,
        selected: n.id === match.id,
      }))
    );

    // Centering calculation (Node is 280x120)
    const x = match.position.x + 140; // 280 / 2
    const y = match.position.y + 60;  // 120 / 2

    setCenter(x, y, {
      zoom: 1.2,
      duration: isInitial ? 800 : 600,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);
    setCurrentMatchIndex(0);
  };

  // Trigger navigation when matches change or index changes
  useEffect(() => {
    if (matches.length > 0 && searchTerm) {
      navigateToMatch(currentMatchIndex, true);
    }
  }, [searchTerm]); // Only on search term change for "initial" find

  const nextMatch = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    if (matches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIndex);
    navigateToMatch(nextIndex, false);
  };

  const prevMatch = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (matches.length === 0) return;
    const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(prevIndex);
    navigateToMatch(prevIndex, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      nextMatch();
    }
    if (e.key === 'Escape') {
      setSearchTerm('');
      inputRef.current?.blur();
    }
  };

  if (!isVisible) return null;

  return (
    <div className="absolute top-4 left-4 z-[60] w-full px-4 md:px-0 md:w-80 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="bg-white/95 backdrop-blur shadow-xl rounded-xl border border-slate-200/50 flex items-center p-1.5 focus-within:ring-2 focus-within:ring-teal-500/30 transition-all">
        <div className="pl-2 text-slate-400">
          <Search size={18} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder="Search schedules (Cmd+F)..."
          className="flex-1 bg-transparent border-none outline-none px-2 py-1.5 text-sm text-slate-900 placeholder:text-slate-400"
        />

        {searchTerm && (
          <div className="flex items-center gap-1 pr-1 border-l border-slate-100 ml-1 pl-2">
            <span className="text-[10px] font-bold text-slate-500 min-w-[32px] text-center">
              {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : '0/0'}
            </span>
            
            <div className="flex">
              <button 
                onClick={prevMatch}
                disabled={matches.length <= 1}
                className="p-1 text-slate-400 hover:text-teal-600 disabled:opacity-30 rounded transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <button 
                onClick={nextMatch}
                disabled={matches.length <= 1}
                className="p-1 text-slate-400 hover:text-teal-600 disabled:opacity-30 rounded transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            <button 
              onClick={() => setSearchTerm('')}
              className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchOverlay;
